import React from "react";
import { toast } from "react-toastify";

import {
  DangerSecondaryButton,
  ErrorDialog,
  PrimaryButton,
} from "@graffer-inc/console-design-kit";

import {
  displayGroupRole,
  displayName,
} from "../../../../../../application/domain/rbac/rbac";

import type { ServiceAccount } from "../../../../../../application/domain/service-account/service-account";

type Props = {
  serviceAccount: ServiceAccount;
  groups: { id: string; name: string }[];
  onClickDisableServiceAccount?: (
    param: { serviceAccountId: ServiceAccount["id"] },
    callback: {
      onSucceeded: () => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;
  onClickStartEdit: () => void;
};

export const ServiceAccountStaticContent: React.FC<Props> = ({
  serviceAccount,
  groups,
  onClickDisableServiceAccount,
  onClickStartEdit,
}) => {
  const [showDisableConfirmModal, setShowDisableConfirmModal] =
    React.useState(false);
  const handleClickDisableMenu = React.useCallback(() => {
    setShowDisableConfirmModal(true);
  }, []);
  const handleProceedDisable = React.useCallback(() => {
    if (!onClickDisableServiceAccount) {
      return;
    }
    onClickDisableServiceAccount(
      { serviceAccountId: serviceAccount.id },
      {
        onSucceeded: () => {
          toast.success("アカウントを無効化しました。");
          setShowDisableConfirmModal(false);
        },
        onFailed: (message) => {
          toast.error(message);
        },
      },
    );
  }, [onClickDisableServiceAccount, serviceAccount]);
  const handleCancelDisable = React.useCallback(() => {
    setShowDisableConfirmModal(false);
  }, []);

  return (
    <section>
      <nav className="level">
        <div className="level-left"></div>
        <div className="level-right">
          {onClickDisableServiceAccount && (
            <p className="level-item">
              <DangerSecondaryButton
                text="アカウントを無効化"
                onClick={handleClickDisableMenu}
                disabled={serviceAccount.disabled}
              />
            </p>
          )}
          <p className="level-item">
            <PrimaryButton text="編集" onClick={onClickStartEdit} />
          </p>
        </div>
      </nav>

      <div className="field">
        <label className="label">アカウント名</label>
        <div className="control">
          <p>{serviceAccount.accountName}</p>
        </div>
      </div>

      <div className="field">
        <label className="label">説明文</label>
        <div className="control">
          <p>{serviceAccount.description}</p>
        </div>
      </div>

      <div className="field">
        <label className="label">ロール設定</label>
        <ul className="control">
          {serviceAccount.roles.map((role) => (
            <li key={role}>{displayName(role)}</li>
          ))}
        </ul>
      </div>

      {serviceAccount.roles.includes("Staff") && (
        <>
          <div className="field">
            <label className="label">所属グループ</label>
            <ul className="control">
              {groups
                .filter(
                  (group) =>
                    serviceAccount.groupsRoles[group.id!] !== undefined,
                )
                .map((group) => (
                  <li key={group.id}>
                    {group.name}&nbsp;:&nbsp;
                    {displayGroupRole(serviceAccount.groupsRoles[group.id!])}
                  </li>
                ))}
            </ul>
          </div>
        </>
      )}

      {showDisableConfirmModal && (
        <ServiceAccountDisableConfirmModal
          onProceed={handleProceedDisable}
          onCancel={handleCancelDisable}
        />
      )}
    </section>
  );
};

const ServiceAccountDisableConfirmModal: React.FC<{
  onProceed: () => void;
  onCancel: () => void;
}> = ({ onProceed, onCancel }) => {
  return (
    <ErrorDialog
      title="アカウントを無効化しようとしています"
      proceedText="無効化"
      onProceed={onProceed}
      cancelText="キャンセル"
      onCancel={onCancel}
      contents={
        <div className="container">
          <p>このアカウントを利用した操作を受け付けなくなります。</p>
          <p>この操作は元に戻すことができません。</p>
        </div>
      }
    />
  );
};
